import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import notify from 'devextreme/ui/notify';
import AntAPI from '../../utils/AntAPI';
import AntAuth from '../../utils/AntAuth';
import AntLocalization from '../../utils/AntLocalization';
import { getInitial, formatTime, renderMarkdown, MessageBlock } from '../AntRightSidebar/shared';
import AntFileViewer from '../AntFileViewer';
import './index.scss';

var MODULE_LABELS = {
    ar: { vi: 'Bán hàng', en: 'Sales', fr: 'Ventes', es: 'Ventas', id: 'Penjualan', ja: '販売', zh: '销售', ko: '판매', hi: 'बिक्री', ar: 'المبيعات' },
    ap: { vi: 'Mua hàng', en: 'Purchase', fr: 'Achats', es: 'Compras', id: 'Pembelian', ja: '仕入', zh: '采购', ko: '구매', hi: 'खरीद', ar: 'المشتريات' },
    ca: { vi: 'Tiền mặt', en: 'Cash', fr: 'Espèces', es: 'Efectivo', id: 'Tunai', ja: '現金', zh: '现金', ko: '현금', hi: 'नकद', ar: 'نقد' },
    ic: { vi: 'Kho', en: 'Inventory', fr: 'Stock', es: 'Inventario', id: 'Stok', ja: '在庫', zh: '库存', ko: '재고', hi: 'स्टॉक', ar: 'المخزون' },
    gl: { vi: 'Kế toán', en: 'Accounting', fr: 'Comptabilité', es: 'Contabilidad', id: 'Akuntansi', ja: '会計', zh: '会计', ko: '회계', hi: 'लेखांकन', ar: 'المحاسبة' },
    hrm: { vi: 'Nhân sự', en: 'HR', fr: 'RH', es: 'RRHH', id: 'SDM', ja: '人事', zh: '人事', ko: '인사', ar: 'الموظفون', hi: 'कर्मचारी' },
    crm: { vi: 'CRM', en: 'CRM', fr: 'GRC', es: 'CRM', id: 'CRM', ja: 'CRM', zh: 'CRM', ko: 'CRM', hi: 'सीआरएम', ar: 'CRM' },
    mf: { vi: 'Sản xuất', en: 'Production', fr: 'Production', es: 'Producción', id: 'Produksi', ja: '製造', zh: '生产', ko: '생산', hi: 'उत्पादन', ar: 'الإنتاج' },
    fa: { vi: 'Tài sản', en: 'Assets', fr: 'Actifs', es: 'Activos', id: 'Aset', ja: '資産', zh: '资产', ko: '자산', hi: 'संपत्ति', ar: 'الأصول' },
    exp: { vi: 'Chi phí', en: 'Expense', fr: 'Dépense', es: 'Gasto', id: 'Biaya', ja: '経費', zh: '费用', ko: '비용', hi: 'खर्च', ar: 'المصروف' },
    master: { vi: 'Danh mục', en: 'Master Data', fr: 'Référentiel', es: 'Datos maestros', id: 'Data master', ja: 'マスタ', zh: '主数据', ko: '기준 정보', hi: 'मास्टर डेटा', ar: 'بيانات أساسية' },
    sys: { vi: 'Hệ thống', en: 'System', fr: 'Système', es: 'Sistema', id: 'Sistem', ja: 'システム', zh: '系统', ko: '시스템', hi: 'सिस्टम', ar: 'النظام' },
    ai: { vi: 'AI', en: 'AI', fr: 'AI', es: 'AI', id: 'AI', ja: 'AI', zh: 'AI', ko: 'AI', hi: 'AI', ar: 'AI' }
};

/**
 * AntAIAssistant — shared AI assistant UI.
 *
 * 3 mode:
 *   - 'floating'  FAB nổi góc phải dưới (tự render FAB + panel khi mở)
 *   - 'embed'     nhúng thẳng vào page (full height/width của container)
 *   - 'drawer'    đặt trong FloatingWorkPanel (right sidebar)
 *
 * Props:
 *   - session            user session (bắt buộc)
 *   - module             filter topic+session theo module ('ar'|'ap'|'ca'|'ic')
 *   - initialTopicCode   pre-select topic
 *   - initialSessionCode deep-link: resume 1 session cũ
 *   - showRail           luôn hiện left rail (default: true cho 'embed')
 *   - onMaximize(ctx)    callback phóng to (ẩn nút nếu undefined)
 *   - onVoucherCreated   callback khi AI tạo phiếu
 */
export default function AntAIAssistant(props) {
    var session = props.session || {};
    var module = props.module || null;
    var mode = props.mode || 'floating';
    var defaultShowRail = mode !== 'drawer';
    var showRail = (typeof props.showRail === 'boolean') ? props.showRail : defaultShowRail;

    var aiAPI = useMemo(function () { return new AntAPI('ai', 'AISession'); }, []);
    var msgAPI = useMemo(function () { return new AntAPI('ai', 'AIMessage'); }, []);
    var topicAPI = useMemo(function () { return new AntAPI('ai', 'AIModuleTopic'); }, []);

    var [open, setOpen] = useState(mode !== 'floating');
    var [railCollapsed, setRailCollapsed] = useState(!showRail);
    var [railWidth, setRailWidth] = useState(330);

    // ── Splitter drag handler ───────────────────────────────────────
    var handleSplitterDown = useCallback(function (e) {
        e.preventDefault();
        var startX = e.clientX;
        var startW = railWidth;
        function onMove(ev) {
            var w = startW + (ev.clientX - startX);
            if (w < 200) w = 200;
            if (w > 520) w = 520;
            setRailWidth(w);
        }
        function onUp() {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.style.userSelect = '';
        }
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [railWidth]);
    var [topics, setTopics] = useState([]);
    var [sessionsAll, setSessionsAll] = useState([]);
    var [selectedTopicCode, setSelectedTopicCode] = useState(props.initialTopicCode || 'general');
    var [railView, setRailView] = useState(props.initialTopicCode ? 'sessions' : 'topics');
    var [sessionId, setSessionId] = useState(null);
    var [sessionCode, setSessionCode] = useState(props.initialSessionCode || null);
    var [sessionTitle, setSessionTitle] = useState('');
    var [messages, setMessages] = useState([]);
    var [input, setInput] = useState('');
    var [streaming, setStreaming] = useState(false);
    var [streamingText, setStreamingText] = useState('');
    var [pendingFiles, setPendingFiles] = useState([]);
    var [sending, setSending] = useState(false);
    var [viewerState, setViewerState] = useState(null);
    var [dragOver, setDragOver] = useState(false);
    var [loading, setLoading] = useState(false);
    var [error, setError] = useState(null);
    var [searchText, setSearchText] = useState('');
    var [moduleFilter, setModuleFilter] = useState(null);

    var fileInputRef = useRef(null);
    var listRef = useRef(null);

    // ── Load topics kèm session count ─────────────────────────────
    // Nếu prop module cố định → load topics của module đó. Else → load all (filter trên FE bằng chip).
    var loadTopics = useCallback(function () {
        if (!open) return;
        var payload = module ? { module: module } : {};
        topicAPI.post('/' + topicAPI.routeURL + '/listMyTopicsWithCount', payload, function (res) {
            var rows = (res && res.data) || res || [];
            var list = Array.isArray(rows) ? rows : (rows.rows || []);
            setTopics(list);
        });
    }, [open, module]);

    useEffect(function () { loadTopics(); }, [loadTopics]);

    // ── Modules distinct từ topics BE trả về (BE đã gate theo settings) ─
    var availableModules = useMemo(function () {
        if (module) return [];
        var seen = {};
        var result = [];
        for (var i = 0; i < topics.length; i++) {
            var m = topics[i].module;
            if (!m || seen[m]) continue;
            seen[m] = true;
            result.push({ code: m, label: MODULE_LABELS[m] || { vi: m.toUpperCase(), en: m.toUpperCase() } });
        }
        return result;
    }, [topics, module]);

    var filteredTopics = useMemo(function () {
        // Không filter ("Tất cả"): tin BE — đã gate theo settings khi seed/list
        if (!moduleFilter) return topics;
        return topics.filter(function (t) { return t.module === moduleFilter; });
    }, [topics, moduleFilter]);

    // ── Load my sessions ────────────────────────────────────────────
    var loadSessions = useCallback(function () {
        if (!open) return;
        aiAPI.post('/' + aiAPI.routeURL + '/listMySessions', { module: module }, function (res) {
            var rows = (res && res.data) || res || [];
            var list = Array.isArray(rows) ? rows : (rows.rows || []);
            setSessionsAll(list);
        });
    }, [open, module]);

    useEffect(function () { loadSessions(); }, [loadSessions]);

    // ── Resume session từ sessionCode (deep-link) ───────────────────
    useEffect(function () {
        if (!open || !sessionCode || sessionId) return;
        setLoading(true);
        aiAPI.post('/' + aiAPI.routeURL + '/detail', { code: sessionCode }, function (res) {
            setLoading(false);
            var data = (res && res.data) || res || {};
            if (data && data.id) {
                setSessionId(data.id);
                setSessionTitle(data.title || '');
                if (data.topic_code) setSelectedTopicCode(data.topic_code);
                _loadHistory(data.id);
            }
        });
    }, [open, sessionCode]);

    function _loadHistory(sid) {
        if (!sid) return;
        msgAPI.post('/' + msgAPI.routeURL + '/list', {
            session_id: sid,
            pageSize: 100,
            ordering: [{ name: 'id', type: 'desc' }]
        }, function (res) {
            var data = (res && res.data) || res || {};
            var rows = (data.rows) || (res && res.rows) || [];
            if (!(rows instanceof Array)) rows = [];
            // Sort theo id asc (giống AIPanel right sidebar)
            rows.sort(function (a, b) { return String(a.id || '0').padStart(18, '0').localeCompare(String(b.id || '0').padStart(18, '0')); });
            setMessages(rows.map(_normalizeMsg));
            _scrollBottom();
        });
    }

    function _normalizeMsg(m) {
        return {
            id: m.id,
            role: m.role || 'assistant',
            content: m.content || '',
            content_type: m.content_type || null,
            content_data: m.content_data || null,
            meddoc_id: m.meddoc_id || null,
            meddoc_name: m.meddoc_name || null,
            file_full_path: m.file_full_path || null,
            file_short_path: m.file_short_path || null,
            createdAt: m.created_at
        };
    }

    function handleBlockAction(action) {
        if (!action) return;
        if (action.type === 'send' && action.content) {
            setInput(action.content);
            setTimeout(handleSend, 30);
        } else if (action.type === 'navigate' && action.url && typeof window !== 'undefined') {
            window.location.hash = action.url.indexOf('#') === 0 ? action.url : '#' + action.url;
        }
    }

    function handleBlockNavigate(url) {
        if (!url || typeof window === 'undefined') return;
        window.location.hash = url.indexOf('#') === 0 ? url : '#' + url;
    }

    function _scrollBottom() {
        setTimeout(function () {
            if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
        }, 40);
    }

    // ── Streaming qua socket ────────────────────────────────────────
    useEffect(function () {
        if (!session || !session.socket || !sessionId) return;
        var socket = session.socket;
        function onStream(data) {
            if (!data || data.event !== 'AISessionStream') return;
            if (data.session_id !== sessionId) return;
            if (data.chunk) setStreamingText(function (prev) { return prev + data.chunk; });
            if (data.done) {
                var finalText = data.content || '';
                setMessages(function (prev) {
                    return prev.concat([{ id: 'stream-' + Date.now(), role: 'assistant', content: finalText, createdAt: Date.now() / 1000 }]);
                });
                setStreamingText('');
                setStreaming(false);
                _scrollBottom();
                // Refresh rail để phiên vừa chat lên đầu danh sách
                loadSessions();
                loadTopics();
            }
        }
        socket.on('data', onStream);
        return function () { socket.off('data', onStream); };
    }, [sessionId, session, loadSessions, loadTopics]);

    // ── Lazy-create session rồi send ───────────────────────────────
    // Pattern giống AIPanel: upload từng file qua /createWithAttachment TRƯỚC,
    // rồi mới gọi AI với text (để Gemini thấy attachment trong history)
    var handleSend = useCallback(function () {
        var content = input.trim();
        if ((!content && pendingFiles.length === 0) || streaming || sending) return;
        var topicCode = selectedTopicCode || 'general';
        setInput('');
        setError(null);

        function uploadFilesThenSend(sid) {
            var files = pendingFiles.slice();
            setPendingFiles([]);
            if (files.length === 0) {
                if (content) _postText(sid, content);
                return;
            }
            setSending(true);
            var i = 0;
            function nextFile() {
                if (i >= files.length) {
                    setSending(false);
                    _loadHistory(sid);
                    if (content) _postText(sid, content);
                    return;
                }
                var fd = new FormData();
                fd.append('files', files[i]);
                fd.append('session_id', sid);
                msgAPI.upload('/' + msgAPI.routeURL + '/createWithAttachment', fd, function () {
                    i++;
                    nextFile();
                });
            }
            nextFile();
        }

        function _postText(sid, text) {
            var localMsg = { id: 'local-' + Date.now(), role: 'user', content: text, createdAt: Date.now() / 1000 };
            setMessages(function (prev) { return prev.concat([localMsg]); });
            setStreaming(true);
            setStreamingText('');
            _scrollBottom();
            _postMessage({
                session_id: sid,
                content: text,
                streaming: !!(session && session.socket)
            });
        }

        if (sessionId) {
            uploadFilesThenSend(sessionId);
        } else {
            aiAPI.post('/' + aiAPI.routeURL + '/create', {
                topic_code: topicCode,
                module: module,
                context_mode: 'topic',
                title: (content || (pendingFiles[0] && pendingFiles[0].name) || '').substring(0, 100)
            }, function (res) {
                var data = (res && res.data) || res || {};
                if (data && data.id) {
                    setSessionId(data.id);
                    setSessionCode(data.code);
                    setSessionTitle(data.title || content.substring(0, 100));
                    uploadFilesThenSend(data.id);
                } else {
                    setError(AntLocalization.translateText({ vi: 'Không thể tạo phiên AI', en: 'Cannot create AI session', fr: 'Création session IA impossible', es: 'No se pudo crear la sesión IA', id: 'Gagal membuat sesi AI', ja: 'AIセッションを作成できません', zh: '无法创建 AI 会话', ko: 'AI 세션을 만들 수 없습니다', hi: 'AI सत्र नहीं बन सका', ar: 'تعذّر إنشاء جلسة AI' }));
                }
            });
        }
    }, [sessionId, sessionCode, selectedTopicCode, input, pendingFiles, streaming, sending, session, module]);

    function _postMessage(body) {
        aiAPI.post('/' + aiAPI.routeURL + '/actionSendMessage', body, function (res) {
            var data = (res && res.data) || res || {};
            if (data.error) {
                setStreaming(false);
                setError(data.message || AntLocalization.translateText({ vi: 'Lỗi AI', en: 'AI error', fr: 'Erreur IA', es: 'Error de IA', id: 'Galat AI', ja: 'AIエラー', zh: 'AI 错误', ko: 'AI 오류', hi: 'AI त्रुटि', ar: 'خطأ AI' }));
                return;
            }
            if (data.streaming) return;
            if (data.assistantMessage) {
                setMessages(function (prev) { return prev.concat([_normalizeMsg(data.assistantMessage)]); });
            }
            if (data.voucherCreated && props.onVoucherCreated) props.onVoucherCreated(data.voucherCreated);
            setStreaming(false);
            _scrollBottom();
            loadSessions();
            loadTopics();
        });
    }

    var handleDragOver = useCallback(function (e) {
        e.preventDefault(); e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        setDragOver(true);
    }, []);
    var handleDragLeave = useCallback(function (e) {
        e.preventDefault(); e.stopPropagation();
        // chỉ clear khi rời hẳn khỏi container (relatedTarget nằm ngoài)
        if (e.currentTarget && e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
        setDragOver(false);
    }, []);
    var handleDrop = useCallback(function (e) {
        e.preventDefault(); e.stopPropagation();
        setDragOver(false);
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            var arr = Array.prototype.slice.call(e.dataTransfer.files);
            setPendingFiles(function (prev) { return prev.concat(arr); });
        }
    }, []);

    function handleFileSelect(e) {
        if (!e.target.files || e.target.files.length === 0) return;
        var arr = Array.prototype.slice.call(e.target.files);
        setPendingFiles(function (prev) { return prev.concat(arr); });
        e.target.value = '';
    }

    function removePendingFile(idx) {
        setPendingFiles(function (prev) { return prev.filter(function (_, i) { return i !== idx; }); });
    }

    // Mở file viewer cho attachments trong messages
    var openFileViewer = useCallback(function (m) {
        var allFiles = messages
            .filter(function (x) { return x.meddoc_id && (x.file_full_path || x.file_short_path); })
            .map(function (x) { return { name: x.meddoc_name || x.content, url: x.file_full_path || x.file_short_path, type: '' }; });
        var idx = 0;
        var targetUrl = m.file_full_path || m.file_short_path;
        for (var i = 0; i < allFiles.length; i++) {
            if (allFiles[i].url === targetUrl) { idx = i; break; }
        }
        if (allFiles.length > 0) setViewerState({ files: allFiles, index: idx });
    }, [messages]);

    // ── Xoá tin nhắn trong session (giữ session) ───────────────────
    function handleClearMessages() {
        if (!sessionId) return;
        if (typeof window !== 'undefined' && !window.confirm(AntLocalization.translateText({ vi: 'Xoá toàn bộ tin nhắn trong phiên này?', en: 'Clear all messages in this session?', fr: 'Effacer tous les messages de cette session ?', es: '¿Borrar todos los mensajes de esta sesión?', id: 'Hapus semua pesan di sesi ini?', ja: 'このセッションの全メッセージを削除しますか？', zh: '清空此会话的所有消息？', ko: '이 세션의 모든 메시지를 삭제할까요?', hi: 'इस सत्र के सभी संदेश हटाएँ?', ar: 'مسح كل رسائل هذه الجلسة؟' }))) return;
        aiAPI.post('/' + aiAPI.routeURL + '/clearMessages', { session_id: sessionId }, function () {
            setMessages([]);
        });
    }

    // ── Xoá session (từ rail) ──────────────────────────────────────
    function handleDeleteSession(s, e) {
        if (e) { e.stopPropagation(); e.preventDefault(); }
        if (!s || !s.id) return;
        aiAPI.post('/' + aiAPI.routeURL + '/delete', { id: s.id }, function (resp) {
            // Phiên mặc định của hệ thống không xoá được (luật configDelete của AISession): hiện lý do, giữ nguyên phiên đang mở.
            if (resp && typeof resp.error === 'string' && resp.error) {
                notify(resp.error, 'error', 5000);
                return;
            }
            loadSessions();
            if (s.id === sessionId) {
                setSessionId(null);
                setSessionCode(null);
                setSessionTitle('');
                setMessages([]);
            }
        });
    }

    function pickSession(s) {
        if (!s || !s.id || s.id === sessionId) return;
        setSessionId(s.id);
        setSessionCode(s.code);
        setSessionTitle(s.title || '');
        setSelectedTopicCode(s.topic_code || selectedTopicCode);
        setMessages([]);
        setStreamingText('');
        setError(null);
        _loadHistory(s.id);
    }

    function startNewSession(topicCode) {
        var tc = topicCode || selectedTopicCode || 'general';
        setSelectedTopicCode(tc);
        setSessionCode(null);
        setSessionId(null);
        setSessionTitle('');
        setMessages([]);
        setStreamingText('');
        setError(null);
    }

    function pickTopic(code) {
        setSelectedTopicCode(code);
        setRailView('sessions');
        setSessionCode(null);
        setSessionId(null);
        setSessionTitle('');
        setMessages([]);
        setStreamingText('');
        setError(null);
    }

    function backToTopics() {
        setRailView('topics');
    }

    // ── Filter sessions theo topic + search text ────────────────────
    var filteredSessions = useMemo(function () {
        var list = sessionsAll || [];
        if (selectedTopicCode) {
            list = list.filter(function (s) {
                var tc = s.topic_code;
                if (!tc || tc === 'general_chat') tc = 'general';
                return tc === selectedTopicCode;
            });
        }
        if (searchText && searchText.trim()) {
            var q = searchText.trim().toLowerCase();
            list = list.filter(function (s) {
                return (s.title || '').toLowerCase().indexOf(q) >= 0 ||
                       (s.code || '').toLowerCase().indexOf(q) >= 0;
            });
        }
        // Sắp xếp: phiên có MESSAGE mới nhất lên trên cùng
        // Ưu tiên timestamp tính từ messages thực tế, fallback session fields
        list = list.slice().sort(function (a, b) {
            var ta = a.last_message_at || a.updated_at || a.created_at || 0;
            var tb = b.last_message_at || b.updated_at || b.created_at || 0;
            return tb - ta;
        });
        return list;
    }, [sessionsAll, selectedTopicCode, searchText]);

    // ── Current topic object ───────────────────────────────────────
    var currentTopic = useMemo(function () {
        return (topics || []).find(function (t) { return t.code === selectedTopicCode; }) || null;
    }, [topics, selectedTopicCode]);

    // ── Render: Floating FAB (mode='floating' chưa mở) ─────────────
    if (mode === 'floating' && !open) {
        return (
            <button
                type="button"
                className="ant-ai-assistant-fab"
                onClick={function () { setOpen(true); }}
                aria-label={AntLocalization.translateText({ vi: 'Mở trợ lý AI', en: 'Open AI assistant', fr: 'Ouvrir l\'assistant IA', es: 'Abrir asistente IA', id: 'Buka asisten AI', ja: 'AIアシスタントを開く', zh: '打开 AI 助手', ko: 'AI 어시스턴트 열기', hi: 'AI सहायक खोलें', ar: 'فتح مساعد AI' })}
            >
                <span className="dx-icon-message"></span>
            </button>
        );
    }

    var containerClass = 'ant-ai-assistant ant-ai-assistant--' + mode + (railCollapsed ? ' ant-ai-assistant--rail-collapsed' : '');
    var breadcrumb = [];
    if (currentTopic) breadcrumb.push(AntLocalization.translateText(currentTopic.name) || currentTopic.code);
    if (sessionId) breadcrumb.push(sessionTitle || AntLocalization.translateText({ vi: 'Phiên hiện tại', en: 'Current session', fr: 'Session en cours', es: 'Sesión actual', id: 'Sesi saat ini', ja: '現在のセッション', zh: '当前会话', ko: '현재 세션', hi: 'वर्तमान सत्र', ar: 'الجلسة الحالية' }));
    else if (selectedTopicCode) breadcrumb.push(AntLocalization.translateText({ vi: 'Phiên mới', en: 'New session', fr: 'Nouv. session', es: 'Nueva sesión', id: 'Sesi baru', ja: '新規セッション', zh: '新会话', ko: '새 세션', hi: 'नया सत्र', ar: 'جلسة جديدة' }));

    return (
        <div className={containerClass}>
            <div className="ant-ai-assistant__header">
                <div className="ant-ai-assistant__title">
                    <button
                        type="button"
                        className="ant-ai-assistant__icon-btn"
                        title={AntLocalization.translateText({ vi: 'Hiện/ẩn danh sách', en: 'Toggle list', fr: 'Voir/masquer la liste', es: 'Mostrar/ocultar lista', id: 'Alihkan daftar', ja: 'リストの表示切替', zh: '显示/隐藏列表', ko: '목록 표시/숨김', hi: 'सूची दिखाएँ/छिपाएँ', ar: 'إظهار/إخفاء القائمة' })}
                        onClick={function () { setRailCollapsed(function (v) { return !v; }); }}
                    >
                        <span className="dx-icon-menu"></span>
                    </button>
                    <div className="ant-ai-assistant__title-text">
                        <div className="ant-ai-assistant__title-main">
                            {/* <span className="dx-icon-message"></span> */}
                            <span>{AntLocalization.translateText(props.title || { vi: 'Trợ lý AI', en: 'AI Assistant', fr: 'Assistant IA', es: 'Asistente IA', id: 'Asisten AI', ja: 'AIアシスタント', zh: 'AI 助手', ko: 'AI 어시스턴트', hi: 'AI सहायक', ar: 'مساعد AI' })}</span>
                        </div>
                        {/* {breadcrumb.length > 0 && (
                            <div className="ant-ai-assistant__breadcrumb">
                                {breadcrumb.map(function (b, i) {
                                    return (
                                        <React.Fragment key={i}>
                                            {i > 0 && <span className="ant-ai-assistant__breadcrumb-sep">›</span>}
                                            <span>{b}</span>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        )} */}
                    </div>
                </div>
                <div className="ant-ai-assistant__actions">
                    <button
                        type="button"
                        className="ant-ai-assistant__icon-btn"
                        title={AntLocalization.translateText({ vi: 'Phiên mới', en: 'New session', fr: 'Nouv. session', es: 'Nueva sesión', id: 'Sesi baru', ja: '新規セッション', zh: '新会话', ko: '새 세션', hi: 'नया सत्र', ar: 'جلسة جديدة' })}
                        onClick={function () { startNewSession(selectedTopicCode || 'general'); }}
                        disabled={streaming}
                    >
                        <span className="dx-icon-add"></span>
                    </button>
                    <button
                        type="button"
                        className="ant-ai-assistant__icon-btn"
                        title={AntLocalization.translateText({ vi: 'Xoá tin nhắn', en: 'Clear messages', fr: 'Supprimer message', es: 'Eliminar mensaje', id: 'Hapus pesan', ja: 'メッセージ削除', zh: '删除消息', ko: '메시지 삭제', hi: 'संदेश हटाएँ', ar: 'حذف الرسالة' })}
                        onClick={handleClearMessages}
                        disabled={!sessionId || streaming}
                    >
                        <span className="dx-icon-trash"></span>
                    </button>
                    {props.onMaximize && (
                        <button
                            type="button"
                            className="ant-ai-assistant__icon-btn"
                            title={AntLocalization.translateText({ vi: 'Mở trang làm việc', en: 'Open workspace', fr: 'Ouvrir l\'espace', es: 'Abrir espacio', id: 'Buka ruang kerja', ja: 'ワークスペースを開く', zh: '打开工作区', ko: '작업 공간 열기', hi: 'कार्यक्षेत्र खोलें', ar: 'فتح مساحة العمل' })}
                            onClick={function () { props.onMaximize({ module: module, sessionCode: sessionCode, topicCode: selectedTopicCode }); }}
                        >
                            <span className="dx-icon-fullscreen"></span>
                        </button>
                    )}
                    {mode === 'floating' && (
                        <button
                            type="button"
                            className="ant-ai-assistant__icon-btn"
                            onClick={function () { setOpen(false); }}
                            title={AntLocalization.translateText({ vi: 'Đóng', en: 'Close', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' })}
                        >
                            <span className="dx-icon-close"></span>
                        </button>
                    )}
                </div>
            </div>

            <div className="ant-ai-assistant__main">
                {!railCollapsed && (
                    <div className="ant-ai-assistant__rail" style={{ width: railWidth, flex: '0 0 ' + railWidth + 'px' }}>
                        {railView === 'topics' && (
                            <div className="ant-ai-assistant__rail-section">
                                <div className="ant-ai-assistant__rail-title">
                                    {AntLocalization.translateText({ vi: 'Chủ đề', en: 'Topics', fr: 'Sujets', es: 'Temas', id: 'Topik', ja: 'トピック', zh: '主题', ko: '주제', hi: 'विषय', ar: 'المواضيع' })}
                                </div>
                                {availableModules.length > 0 && (
                                    <div className="ant-ai-assistant__rail-modulefilter">
                                        <button
                                            type="button"
                                            className={'ant-ai-assistant__rail-modulechip' + (!moduleFilter ? ' is-active' : '')}
                                            onClick={function () { setModuleFilter(null); }}
                                        >
                                            {AntLocalization.translateText({ vi: 'Tất cả', en: 'All', fr: 'Tout', es: 'Todo', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' })}
                                        </button>
                                        {availableModules.map(function (m) {
                                            return (
                                                <button
                                                    key={m.code}
                                                    type="button"
                                                    className={'ant-ai-assistant__rail-modulechip' + (moduleFilter === m.code ? ' is-active' : '')}
                                                    onClick={function () { setModuleFilter(m.code); }}
                                                >
                                                    {AntLocalization.translateText(m.label)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                <div className="ant-ai-assistant__rail-list">
                                    {filteredTopics.map(function (t) {
                                        var count = t.session_count || 0;
                                        return (
                                            <div
                                                key={t.code}
                                                className="ant-ai-assistant__rail-topic"
                                                onClick={function () { pickTopic(t.code); }}
                                                title={AntLocalization.translateText(t.description) || ''}
                                            >
                                                {/* <span className={'ant-ai-assistant__rail-topic-icon dx-icon-' + (t.icon || 'message')}></span> */}
                                                <div className="ant-ai-assistant__rail-topic-main">
                                                    <span className="ant-ai-assistant__rail-topic-name">
                                                        {AntLocalization.translateText(t.name) || t.code}
                                                    </span>
                                                    {t.description && (
                                                        <span className="ant-ai-assistant__rail-topic-desc">
                                                            {AntLocalization.translateText(t.description)}
                                                        </span>
                                                    )}
                                                </div>
                                                {count > 0 && (
                                                    <span className="ant-ai-assistant__rail-topic-badge">{count}</span>
                                                )}
                                                <span className="ant-ai-assistant__rail-topic-arrow dx-icon-chevronright"></span>
                                            </div>
                                        );
                                    })}
                                    {filteredTopics.length === 0 && (
                                        <div className="ant-ai-assistant__rail-empty">
                                            {AntLocalization.translateText({ vi: 'Chưa có chủ đề', en: 'No topics', fr: 'Aucun sujet', es: 'Sin temas', id: 'Belum ada topik', ja: 'トピックなし', zh: '暂无主题', ko: '주제 없음', hi: 'कोई विषय नहीं', ar: 'لا توجد مواضيع' })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {railView === 'sessions' && (
                            <div className="ant-ai-assistant__rail-section ant-ai-assistant__rail-section--sessions">
                                <div className="ant-ai-assistant__rail-header">
                                    <button
                                        type="button"
                                        className="ant-ai-assistant__rail-back"
                                        onClick={backToTopics}
                                        title={AntLocalization.translateText({ vi: 'Quay lại', en: 'Back', fr: 'Retour', es: 'Volver', id: 'Kembali', ja: '戻る', zh: '返回', ko: '뒤로', hi: 'वापस', ar: 'رجوع' })}
                                    >
                                        <span className="dx-icon-chevronleft"></span>
                                    </button>
                                    <div className="ant-ai-assistant__rail-header-text">
                                        {/* {currentTopic && (
                                            <span className={'ant-ai-assistant__rail-header-icon dx-icon-' + (currentTopic.icon || 'message')}></span>
                                        )} */}
                                        <span className="ant-ai-assistant__rail-header-name">
                                            {currentTopic ? (AntLocalization.translateText(currentTopic.name) || currentTopic.code)
                                                : AntLocalization.translateText({ vi: 'Phiên', en: 'Sessions', fr: 'Sessions', es: 'Sesiones', id: 'Sesi', ja: 'セッション', zh: '会话', ko: '세션', hi: 'सत्र', ar: 'الجلسات' })}
                                        </span>
                                    </div>
                                </div>
                                <div className="ant-ai-assistant__rail-search">
                                    {/* <span className="dx-icon-search"></span> */}
                                    <input
                                        type="text"
                                        value={searchText}
                                        onChange={function (e) { setSearchText(e.target.value); }}
                                        placeholder={AntLocalization.translateText({ vi: 'Tìm phiên...', en: 'Search sessions...', fr: 'Chercher session...', es: 'Buscar sesiones...', id: 'Cari sesi...', ja: 'セッションを検索...', zh: '搜索会话...', ko: '세션 검색...', hi: 'सत्र खोजें...', ar: 'بحث في الجلسات...' })}
                                    />
                                </div>
                                <div className="ant-ai-assistant__rail-list">
                                    {filteredSessions.map(function (s) {
                                        var active = s.id === sessionId;
                                        var label = s.title || s.code;
                                        return (
                                            <div
                                                key={s.id}
                                                className={'ant-ai-assistant__rail-item ant-ai-assistant__rail-item--session' + (active ? ' is-active' : '')}
                                                onClick={function () { pickSession(s); }}
                                                title={label}
                                            >
                                                <div className="ant-ai-assistant__rail-item-main">
                                                    <span className="ant-ai-assistant__rail-item-text">{label}</span>
                                                    <span className="ant-ai-assistant__rail-item-meta">
                                                        {formatTime(s.last_message_at || s.created_at)}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="ant-ai-assistant__rail-item-delete"
                                                    onClick={function (e) { handleDeleteSession(s, e); }}
                                                    title={AntLocalization.translateText({ vi: 'Xoá', en: 'Delete', fr: 'Supprimer', es: 'Eliminar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएं', ar: 'حذف' })}
                                                >
                                                    <span className="dx-icon-trash"></span>
                                                </button>
                                            </div>
                                        );
                                    })}
                                    {filteredSessions.length === 0 && (
                                        <div className="ant-ai-assistant__rail-empty">
                                            {AntLocalization.translateText({ vi: 'Chưa có phiên — bấm + để tạo', en: 'No sessions — click + to start', fr: 'Aucune session — + pour en créer', es: 'Sin sesiones — pulse + para crear', id: 'Belum ada sesi — klik + untuk membuat', ja: 'セッションなし — + で作成', zh: '暂无会话 — 点击 + 创建', ko: '세션 없음 — +를 눌러 생성', hi: 'कोई सत्र नहीं — बनाने हेतु + दबाएँ', ar: 'لا جلسات — اضغط + للإنشاء' })}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="ant-ai-assistant__rail-fab"
                                    onClick={function () { startNewSession(selectedTopicCode); }}
                                    title={AntLocalization.translateText({ vi: 'Phiên mới trong chủ đề này', en: 'New session in this topic', fr: 'Nouvelle session dans ce sujet', es: 'Nueva sesión en este tema', id: 'Sesi baru di topik ini', ja: 'このトピックで新規セッション', zh: '在此主题下新建会话', ko: '이 주제에서 새 세션', hi: 'इस विषय में नया सत्र', ar: 'جلسة جديدة في هذا الموضوع' })}
                                >
                                    <span className="dx-icon-add"></span>
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {!railCollapsed && (
                    <div
                        className="ant-ai-assistant__splitter"
                        onMouseDown={handleSplitterDown}
                        title={AntLocalization.translateText({ vi: 'Kéo để thay đổi kích thước', en: 'Drag to resize', fr: 'Glisser pour redimensionner', es: 'Arrastre para redimensionar', id: 'Seret untuk ubah ukuran', ja: 'ドラッグしてサイズ変更', zh: '拖动以调整大小', ko: '드래그하여 크기 조절', hi: 'आकार बदलने हेतु खींचें', ar: 'اسحب لتغيير الحجم' })}
                    />
                )}

                <div
                    className={'ant-ai-assistant__chat' + (dragOver ? ' is-drag-over' : '')}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {dragOver && (
                        <div className="ant-ai-assistant__drop-overlay">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                            <span>{AntLocalization.translateText({ vi: 'Thả file để đính kèm', en: 'Drop file to attach', fr: 'Déposer pour joindre', es: 'Suelte para adjuntar', id: 'Lepas untuk melampirkan', ja: 'ドロップして添付', zh: '拖放文件以添加附件', ko: '파일을 놓아 첨부', hi: 'फ़ाइल यहाँ छोड़ें', ar: 'أفلت الملف لإرفاقه' })}</span>
                        </div>
                    )}
                    <div className="chat-messages" ref={listRef}>
                        {loading && (
                            <div className="ant-ai-assistant__loading">
                                {AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '불러오는 중...', hi: 'लोड जारी...', ar: 'قيد التحميل...' })}
                            </div>
                        )}
                        {!loading && messages.length === 0 && !streamingText && !sessionId && currentTopic && (
                            <div className="ant-ai-assistant__empty-topic">
                                <div className="ant-ai-assistant__empty-icon">
                                    <span className={'dx-icon-' + (currentTopic.icon || 'message')}></span>
                                </div>
                                <div className="ant-ai-assistant__empty-title">
                                    {AntLocalization.translateText(currentTopic.name) || currentTopic.code}
                                </div>
                                {currentTopic.description && (
                                    <div className="ant-ai-assistant__empty-desc">
                                        {AntLocalization.translateText(currentTopic.description)}
                                    </div>
                                )}
                                <div className="ant-ai-assistant__empty-hint">
                                    {AntLocalization.translateText({ vi: 'Gõ câu hỏi đầu tiên để tạo phiên', en: 'Type your first question to start a session', fr: 'Posez votre première question pour démarrer', es: 'Escriba su primera pregunta para iniciar una sesión', id: 'Ketik pertanyaan pertama untuk memulai sesi', ja: '最初の質問を入力してセッションを開始', zh: '输入第一个问题以创建会话', ko: '첫 질문을 입력해 세션을 시작하세요', hi: 'सत्र शुरू करने हेतु पहला प्रश्न लिखें', ar: 'اكتب سؤالك الأول لبدء جلسة' })}
                                </div>
                            </div>
                        )}
                        {!loading && messages.length === 0 && !streamingText && !sessionId && !currentTopic && (
                            <div className="ant-ai-assistant__empty">
                                {AntLocalization.translateText({ vi: 'Chọn chủ đề để bắt đầu', en: 'Pick a topic to begin', fr: 'Choisissez un sujet', es: 'Elija un tema para empezar', id: 'Pilih topik untuk memulai', ja: 'トピックを選んで開始', zh: '选择主题以开始', ko: '주제를 선택해 시작하세요', hi: 'शुरू करने हेतु विषय चुनें', ar: 'اختر موضوعًا للبدء' })}
                            </div>
                        )}
                        {messages.map(function (m, mi) {
                            var isUser = m.role === 'user';
                            var prev = mi > 0 ? messages[mi - 1] : null;
                            var sameRoleAsPrev = prev && prev.role === m.role;
                            var showHeader = !sameRoleAsPrev;
                            var userName = isUser
                                ? (session && session.identity && session.identity.user_name) || AntLocalization.translateText({ vi: 'Bạn', en: 'You', fr: 'Vous', es: 'Tú', id: 'Anda', ja: 'あなた', zh: '您', ko: '나', hi: 'आप', ar: 'أنت' })
                                : AntLocalization.translateText({ vi: 'Trợ lý AI', en: 'AI Assistant', fr: 'Assistant IA', es: 'Asistente IA', id: 'Asisten AI', ja: 'AIアシスタント', zh: 'AI 助手', ko: 'AI 어시스턴트', hi: 'AI सहायक', ar: 'مساعد AI' });

                            var hasMeddoc = m.meddoc_id && m.meddoc_name;
                            var fileUrl = m.file_full_path || m.file_short_path;
                            var isImage = hasMeddoc && fileUrl && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(m.meddoc_name);
                            var fileExt = hasMeddoc ? (m.meddoc_name || '').split('.').pop().toLowerCase() : '';
                            var fileLabel = fileExt === 'pdf' ? 'PDF'
                                : (fileExt === 'doc' || fileExt === 'docx') ? 'DOC'
                                : (fileExt === 'xls' || fileExt === 'xlsx') ? 'XLS'
                                : fileExt.toUpperCase();
                            return (
                                <div key={m.id} className={'chat-msg' + (sameRoleAsPrev ? ' chat-msg-grouped' : '') + (isUser ? ' chat-msg--user' : ' chat-msg--ai')}>
                                    {showHeader && !isUser ? (
                                        <div className="chat-msg-avatar">
                                            <span style={{ background: 'linear-gradient(135deg,#4f46e5,#6366f1)', color: '#fff', width: '100%', height: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontSize: 11, fontWeight: 600 }}>AI</span>
                                        </div>
                                    ) : showHeader && isUser ? (
                                        <div className="chat-msg-avatar">{getInitial(userName)}</div>
                                    ) : (
                                        <div className="chat-msg-avatar-spacer" />
                                    )}
                                    <div className="chat-msg-body">
                                        {showHeader && (
                                            <div className="chat-msg-meta">
                                                <span className="chat-msg-author">{userName}</span>
                                                {m.createdAt && <span className="chat-msg-time">{formatTime(m.createdAt)}</span>}
                                            </div>
                                        )}
                                        {m.content_type !== 'file' && m.content && (
                                            <div dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }} />
                                        )}
                                        {hasMeddoc && fileUrl && (
                                            <div className="chat-msg-attachments">
                                                {isImage ? (
                                                    <img
                                                        className="chat-att-image"
                                                        src={fileUrl}
                                                        alt={m.meddoc_name || ''}
                                                        onClick={function () { openFileViewer(m); }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="att-file-card"
                                                        onClick={function () { openFileViewer(m); }}
                                                        title={m.meddoc_name}
                                                    >
                                                        <div className="att-file-card-icon">
                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                                                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                                                <polyline points="14 2 14 8 20 8" />
                                                            </svg>
                                                            <span className="att-file-card-ext">{fileLabel}</span>
                                                        </div>
                                                        <div className="att-file-card-name">{m.meddoc_name}</div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {hasMeddoc && !fileUrl && (
                                            <div className="chat-msg-attachments">
                                                <span className="chat-att-deleted">
                                                    📎 {m.meddoc_name} <em>({AntLocalization.translateText({ vi: 'đã xoá', en: 'deleted', fr: 'supprimé', es: 'eliminado', id: 'dihapus', ja: '削除済', zh: '已删除', ko: '삭제됨', hi: 'हटाया गया', ar: 'محذوف' })})</em>
                                                </span>
                                            </div>
                                        )}
                                        {m.content_data && (function () {
                                            try {
                                                var blocks = typeof m.content_data === 'string' ? JSON.parse(m.content_data) : m.content_data;
                                                if (!blocks) return null;
                                                if (!(blocks instanceof Array)) blocks = [blocks];
                                                return blocks.map(function (block, bi) {
                                                    return <MessageBlock key={bi} block={block} onAction={handleBlockAction} onNavigate={handleBlockNavigate} />;
                                                });
                                            } catch (e) { return null; }
                                        })()}
                                    </div>
                                </div>
                            );
                        })}
                        {streamingText && (
                            <div className="chat-msg">
                                <div className="chat-msg-avatar">
                                    <span style={{ background: 'linear-gradient(135deg,#4f46e5,#6366f1)', color: '#fff', width: '100%', height: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontSize: 11, fontWeight: 600 }}>AI</span>
                                </div>
                                <div className="chat-msg-body">
                                    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(streamingText) }} />
                                </div>
                            </div>
                        )}
                        {streaming && !streamingText && (
                            <div className="chat-typing-indicator">
                                <span className="chat-typing-dot" />
                                <span className="chat-typing-dot" />
                                <span className="chat-typing-dot" />
                                <span className="chat-typing-label">
                                    {AntLocalization.translateText({ vi: 'AI đang trả lời...', en: 'AI is typing...', fr: 'IA répond...', es: 'IA está escribiendo...', id: 'AI sedang menjawab...', ja: 'AIが回答中...', zh: 'AI 正在回复...', ko: 'AI가 답변 중...', hi: 'AI उत्तर दे रहा है...', ar: 'AI يكتب...' })}
                                </span>
                            </div>
                        )}
                        {error && (<div className="ant-ai-assistant__error">{error}</div>)}
                    </div>

                    <div className="chat-input-area">
                        {pendingFiles.length > 0 && (
                            <div className="chat-file-preview-list">
                                {pendingFiles.map(function (f, fi) {
                                    return (
                                        <div key={fi} className="chat-file-preview">
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                            </svg>
                                            <span className="file-preview-name">{f.name}</span>
                                            <button
                                                className="file-preview-close"
                                                onClick={function () { removePendingFile(fi); }}
                                                title={AntLocalization.translateText({ vi: 'Gỡ', en: 'Remove', fr: 'Retirer', es: 'Quitar', id: 'Hapus', ja: '削除', zh: '移除', ko: '제거', hi: 'हटाएँ', ar: 'إزالة' })}
                                            >
                                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className="chat-input-toolbar">
                            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
                            <button
                                className="chat-tool-btn"
                                onClick={function () { fileInputRef.current && fileInputRef.current.click(); }}
                                title={AntLocalization.translateText({ vi: 'Đính kèm', en: 'Attach', fr: 'Joindre', es: 'Adjuntar', id: 'Lampirkan', ja: '添付', zh: '添加附件', ko: '첨부', hi: 'संलग्न करें', ar: 'إرفاق' })}
                                disabled={streaming}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                            </button>
                            <span style={{ flex: 1 }} />
                            <button
                                className={'chat-tool-btn chat-send-btn' + ((input.trim() || pendingFiles.length > 0) ? ' active' : '')}
                                onClick={handleSend}
                                disabled={streaming || sending || (!input.trim() && pendingFiles.length === 0)}
                                title={AntLocalization.translateText({ vi: 'Gửi', en: 'Send', fr: 'Envoyer', es: 'Enviar', id: 'Kirim', ja: '送信', zh: '发送', ko: '보내기', hi: 'भेजें', ar: 'إرسال' })}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                                </svg>
                            </button>
                        </div>
                        <div className="chat-input-row">
                            <textarea
                                className="chat-input"
                                value={input}
                                onChange={function (e) { setInput(e.target.value); }}
                                onKeyDown={function (e) {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                placeholder={AntLocalization.translateText({ vi: 'Nhập tin nhắn...', en: 'Type a message...', fr: 'Entrez un message...', es: 'Ingrese mensaje...', id: 'Masukkan pesan...', ja: 'メッセージを入力...', zh: '输入消息...', ko: '메시지 입력...', hi: 'संदेश दर्ज करें...', ar: 'أدخل رسالة...' })}
                                rows={2}
                                disabled={streaming}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {viewerState && (
                <AntFileViewer
                    visible
                    files={viewerState.files}
                    initialIndex={viewerState.index}
                    onClose={function () { setViewerState(null); }}
                />
            )}
        </div>
    );
}
